import { AssetStatus, AssetType } from "@amzn/ads-magneto-typescript-client";
import type { GeneratedInsight } from "@amzn/d16-g-client-deck-builder-typescript-client";
import {
    InsightOutputFormat,
    InsightType,
} from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useAsync } from "@react-hookz/web";
import { isEmpty } from "lodash";
import pWaitFor from "p-wait-for";
import React, { useCallback } from "react";
import { useStore } from "zustand";

import { getBlinkApiClientV2 } from "../../../../../api/blinkApi/blinkApi";
import { getMagnetoApi } from "../../../../../api/magnetoApi/magnetoApi";
import { magnetoS3LocationToDownloadAsset } from "../../../../../hooks/generatedInsights/useGeneratedInsightsAction";
import { useInsightIdentifierEffect } from "../../../../../hooks/misc/useInsightIdentifierEffect";
import { useAsyncErrorStatusNotification } from "../../../../../hooks/notifications/useAsyncErrorStatusNotification";
import { authStore } from "../../../../../store/auth";
import { FeatureFlagsStore } from "../../../../../store/featureFlags";
import { globalWorkbenchStore } from "../../../../../store/globalWorkbench";
import { insightDetailsStore } from "../../../../../store/insightDetails";
import { reportIssueStore } from "../../../../../store/reportIssue";
import { workbenchStore } from "../../../../../store/workbench";
import { AquilaThumbsFeedbackPlacement } from "../../../../../types/aquila";
import { AquilaReportIssueTriggerType } from "../../../../../types/aquila";
import type { CardDetail } from "../../../../../types/card";
import type { DownloadAsset } from "../../../../../types/common";
import { InsightPreviewFileFormats } from "../../../../../types/insightPreviewFileFormats";
import { Progress } from "../../../../../types/progress";
import { isLoading, isNotExecuted } from "../../../../../utils/api.utils";
import { logViewInsightDetailsClick } from "../../../../../utils/telemetry.utils";
import { AquilaThumbsFeedback } from "../../../../aquilaThumbsFeedback/AquilaThumbsFeedback";
import {
    DownloadErrorNotification,
    PreviewErrorNotification,
} from "../../../../common/ErrorNotificationWithLink";
import { StyledActionsContainer } from "../../../../common/styled.components";
import { InsightSelectionActionText } from "../../InsightSelectionActionText";
import { VisualizationSuccessAction } from "../VisualizationSuccessAction";
import { WbVisualizationFailedAction } from "../WbVisualizationFailedAction";

export interface CuratedInsightCardActionProps {
    executionId: string;
    advertiserId: string;
    title: string;
    details: CardDetail[];
    progress: Progress;
    description?: string;
    index?: number;
    outputFileFormats?: string[];
    insight?: GeneratedInsight;
    isGlobalWorkbench?: boolean;
}

export const CuratedInsightCardAction: React.FunctionComponent<
    CuratedInsightCardActionProps
> = ({
    executionId,
    advertiserId,
    progress,
    title,
    details,
    description,
    index,
    outputFileFormats = [],
    insight = {} as GeneratedInsight,
    isGlobalWorkbench = false,
}) => {
    const [setInsightDetails, setAsyncStatus] = useStore(
        insightDetailsStore,
        (state) => [state.setInsightDetails, state.setAsyncStatus],
    );
    const [setReportIssueContextData, openModal] = useStore(
        reportIssueStore,
        (state) => [state.setReportIssueContextData, state.openModal],
    );
    const [thumbsFeedbackCard] = useStore(FeatureFlagsStore, (state) => [
        state.thumbsFeedbackCard,
    ]);

    const [getSlidesSelected] = useStore(globalWorkbenchStore, (state) => [
        state.getSlidesSelected,
    ]);

    const [getSlideIndexes] = useStore(workbenchStore, (state) => [
        state.getSlideIndexes,
    ]);

    const [requesterAlias] = useStore(authStore, (state) => [state.username]);

    const [previewAsset, setPreviewAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);
    const [downloadAsset, setDownloadAsset] = React.useState<
        DownloadAsset | undefined
    >(undefined);

    const [previewGenerating, setPreviewGenerating] =
        React.useState<boolean>(false);
    const [downloadGenerating, setDownloadGenerating] =
        React.useState<boolean>(false);

    const disabled = Object.values({
        executionId,
        advertiserId,
    }).some((val) => isEmpty(val));

    const [downloadState, downloadAction] = useAsync(async () => {
        if (isGlobalWorkbench) {
            await pWaitFor(
                async () => {
                    const { data } = await (
                        await getMagnetoApi()
                    ).getDeckAsset({
                        deckId: executionId,
                        workbenchScope: advertiserId,
                        assetType: AssetType.PPTX,
                    });
                    if (data && data.status === AssetStatus.SUCCESS) {
                        const pptx = magnetoS3LocationToDownloadAsset(data);
                        setDownloadAsset(pptx);
                        setDownloadGenerating(false);
                        return true;
                    } else if (
                        data &&
                        data.status === AssetStatus.IN_PROGRESS
                    ) {
                        setDownloadGenerating(true);
                        return false;
                    } else if (data && data.status === AssetStatus.ERROR) {
                        setDownloadGenerating(false);
                        throw new Error("download failed to generate");
                    } else {
                        return false;
                    }
                },
                {
                    interval: 1000,
                    timeout: 300000,
                },
            );
        } else {
            const outputFormat = outputFileFormats.includes("zip")
                ? InsightOutputFormat.ZIP
                : InsightOutputFormat.PPTX;
            const data = await (
                await getBlinkApiClientV2()
            ).getPackagedInsightDownload({
                requesterAlias,
                executionId,
                advertiserId,
                outputFormat,
            });
            setDownloadAsset(data as DownloadAsset);
        }
    });

    useAsyncErrorStatusNotification(
        downloadState,
        [],
        {
            Error: (
                <DownloadErrorNotification
                    onReportClick={() =>
                        openModal(AquilaReportIssueTriggerType.SIDE_PANEL)
                    }
                />
            ),
        },
        executionId,
        (error) => {
            setReportIssueContextData({
                ...insight,
                apiError: {
                    requestUrl: isGlobalWorkbench
                        ? "getMagnetoApi getDeckAsset (download)"
                        : "getGeneratedInsightDownload API (download)",
                    errorResponse: error.message || JSON.stringify(error),
                    errorCode: error.statusCode || error.code || "Unknown",
                },
            });
        },
    );

    const [previewState, previewAction] = useAsync(async () => {
        if (isGlobalWorkbench) {
            await pWaitFor(
                async () => {
                    const { data } = await (
                        await getMagnetoApi()
                    ).getDeckAsset({
                        deckId: executionId,
                        workbenchScope: advertiserId,
                        assetType: AssetType.PDF,
                    });
                    if (data && data.status === AssetStatus.SUCCESS) {
                        const pdfDownload =
                            magnetoS3LocationToDownloadAsset(data);
                        setPreviewAsset(pdfDownload);
                        setPreviewGenerating(false);
                        return true;
                    } else if (
                        data &&
                        data.status === AssetStatus.IN_PROGRESS
                    ) {
                        setPreviewGenerating(true);
                        return false;
                    } else if (data && data.status === AssetStatus.ERROR) {
                        setPreviewGenerating(false);
                        throw new Error("preview failed to generate");
                    } else {
                        return false;
                    }
                },
                {
                    interval: 1000,
                    timeout: 300000,
                },
            );
        } else {
            const data = await (
                await getBlinkApiClientV2()
            ).getPackagedInsightDownload({
                requesterAlias,
                executionId,
                advertiserId,
                outputFormat: InsightOutputFormat.PDF,
            });
            setPreviewAsset(data as DownloadAsset);
        }
    });

    useAsyncErrorStatusNotification(
        previewState,
        [],
        {
            Error: (
                <PreviewErrorNotification
                    onReportClick={() =>
                        openModal(AquilaReportIssueTriggerType.SIDE_PANEL)
                    }
                />
            ),
        },
        executionId,
        (error) => {
            setReportIssueContextData({
                ...insight,
                apiError: {
                    requestUrl: isGlobalWorkbench
                        ? "getMagnetoApi getDeckAsset (preview)"
                        : "getGeneratedInsightDownload API (preview)",
                    errorResponse: error.message || JSON.stringify(error),
                    errorCode: error.statusCode || error.code || "Unknown",
                },
            });
        },
    );

    const handleInsightDetails = useCallback(async (): Promise<void> => {
        logViewInsightDetailsClick(executionId, (index ?? 0) + 1);
        void previewAction.execute();
        void downloadAction.execute();
        setReportIssueContextData({ ...insight });
    }, [executionId, index, insight]);

    useInsightIdentifierEffect(executionId, handleInsightDetails);

    React.useEffect(() => {
        if (
            !isNotExecuted(previewState.status) ||
            !isNotExecuted(downloadState.status)
        ) {
            setInsightDetails({
                open: true,
                info: {
                    title,
                    details,
                    previewFileFormat: InsightPreviewFileFormats.PDF,
                    outputFileFormats,
                    insightType: InsightType.WB,
                    identifier: executionId,
                    previewAsset,
                    downloadAsset,
                    description,
                    previewLoading: isLoading(previewState.status),
                    previewGenerating,
                    downloadLoading:
                        downloadGenerating || isLoading(downloadState.status),
                },
            });
        }
    }, [
        previewAsset,
        downloadAsset,
        previewState.status,
        downloadState.status,
        previewGenerating,
        downloadGenerating,
    ]);

    const numSelectedSlides = getSlidesSelected(executionId).length;

    return (
        <StyledActionsContainer>
            {(() => {
                switch (progress) {
                    case Progress.VISUALIZATION_FAILED:
                        return (
                            <WbVisualizationFailedAction
                                executionId={executionId}
                            />
                        );
                    case Progress.VISUALIZATION_WARNING:
                        return (
                            <VisualizationSuccessAction
                                executionId={executionId}
                                disabled={disabled}
                                handleInsightDetails={handleInsightDetails}
                                warning
                            />
                        );
                    default:
                        return (
                            <VisualizationSuccessAction
                                executionId={executionId}
                                disabled={disabled}
                                handleInsightDetails={handleInsightDetails}
                            />
                        );
                }
            })()}
            {numSelectedSlides > 0 && (
                <InsightSelectionActionText
                    identifier={executionId}
                    numSelectedSlides={numSelectedSlides}
                />
            )}
            {numSelectedSlides === 0 && thumbsFeedbackCard && (
                <AquilaThumbsFeedback
                    identifier={executionId}
                    insightType={InsightType.WB}
                    placement={AquilaThumbsFeedbackPlacement.CARD}
                />
            )}
        </StyledActionsContainer>
    );
};
